// First, before any module that touches a schema: see zod-config.ts.
import './zod-config.ts'
import { StrictMode } from 'react'
import { createRoot, hydrateRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router'
import { App } from './App.tsx'
import { I18nProvider } from './i18n/index.tsx'
import { SEED_ELEMENT_ID, SeedProvider, setBrowserSeed, type PageSeed } from './lib/seed.ts'
import './styles.css'

const container = document.getElementById('root')
if (!container) throw new Error('#root missing from index.html')

// A page the Worker rendered carries the data it was rendered from
// (lib/seed.ts, worker/render-body.ts). Seeded before the first render, React
// builds the same page from the same data, with no request, and *hydrates*:
// it adopts the server's HTML rather than drawing the page a second time. A
// second drawing would make the browser count the page's largest paint from
// then, which is most of what server rendering is for (#74). A page without
// a seed renders as before.
//
// Where the browser knows more than the server did, the first render must
// still match the server's: React does not patch a mismatch, it throws the
// server's page away. So what only the browser knows is read after hydration:
// the reader's language, for one, through a server snapshot (i18n/index.tsx).
const seedElement = document.getElementById(SEED_ELEMENT_ID)
let seeded = false
let seed: PageSeed = {}
if (seedElement?.textContent) {
  try {
    seed = JSON.parse(seedElement.textContent) as PageSeed
    // The tree reads it through SeedProvider below; the effect-time fetches
    // read this copy (lib/seed.ts).
    setBrowserSeed(seed)
    seeded = true
  } catch {
    // An unreadable seed is no seed: the page fetches its data as it always did.
  }
}

const page = (
  <StrictMode>
    <SeedProvider seed={seed}>
      <I18nProvider>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </I18nProvider>
    </SeedProvider>
  </StrictMode>
)

if (seeded) hydrateRoot(container, page)
else createRoot(container).render(page)
